
import { Prop, Vue } from 'vue-property-decorator';
import PnpItem from '../models/PnpItem';
import PlayerEmitter from '../event/PlayerEmitter';
import EventBusConstants from '@/constants/EventBusConstants';
import { inject } from 'vue';
import ToolManager from '@/controllers/tools/ToolManager';
import Test from '@/models/Test';
import DeliveryOptionConstants from '@/controllers/options/DeliveryOptionConstants';
import TextHelp from '../util/TextHelp';

export default class PnpExpandingToolbar extends Vue {
  private hideNames = true;
  private initialized = false;

  private expanded = false;
  private hideExpandButton_ = false;
  private playerEmitter: PlayerEmitter | undefined = inject('playerEmitter');
  private toolManager: ToolManager | undefined = inject<ToolManager>('toolManager');

  private readonly availableTools: Array<string> = [
    DeliveryOptionConstants.COLOR_SCHEME,
    DeliveryOptionConstants.FONT_SIZE,
    DeliveryOptionConstants.ZOOM,
    DeliveryOptionConstants.LINE_READER,
    DeliveryOptionConstants.NOTEPAD,
    DeliveryOptionConstants.FLAG_ITEM,
    'basic-calculator',
    'scientific-calculator',
    'graphing-calculator',
  ];

  private readonly basicTools: Array<string> = [
    DeliveryOptionConstants.FLAG_ITEM,
    'basic-calculator',
    'scientific-calculator',
    'graphing-calculator',
    DeliveryOptionConstants.NOTEPAD,
  ];

  @Prop({ default: false }) readonly focusMode: boolean | undefined;
  @Prop({ default: undefined }) readonly test: Test | undefined;

  private getBasicTools(): Array<PnpItem> {
    let tools = this.toolManager!.getPnpArray().filter((item) => this.basicTools.indexOf(item.name) !== -1);
    return tools;
  }

  private showButton(name: string): boolean {
    return this.toolManager!.getPnpArray().filter((item) => item.name === name).length > 0;
  }

  mounted() {
    window.addEventListener('resize', this.windowSizeChanged);
    this.windowSizeChanged();
    this.playerEmitter?.on(EventBusConstants.NAVIGATION_EVENT, () => {
      // Close any open options modals
      this.$el.querySelector('.menu-button.show-options')?.classList.remove('show-options');
      this.$el.querySelector('.menu-button.selected')?.classList.remove('selected');
    });
    this.playerEmitter?.on(EventBusConstants.ITEM_PICKED, () => {
      // Close any open options modals
      this.$el.querySelector('.menu-button.show-options')?.classList.remove('show-options');
      this.$el.querySelector('.menu-button.selected')?.classList.remove('selected');
    });
  }

  // Shows the options for a given button
  toggleTool(buttonID: string) {
    // Tool that was clicked
    let clickedButton = this.$el.querySelector(`#${buttonID}`);
    if (!clickedButton) {
      console.warn('Invalid tool option selected', buttonID);
      return;
    }

    let hasOptions = clickedButton.querySelector('.tool-modal') != undefined;
    let isSelected = clickedButton.classList.contains('show-options') || clickedButton.classList.contains('selected');
    //console.log(buttonID, hasOptions, isSelected);

    // Buttons with option menus
    if (hasOptions) {
      // Close any open options modals
      this.$el.querySelector('.menu-button.show-options')?.classList.remove('show-options');

      // Open options for clicked if not already selected
      if (!isSelected) clickedButton.classList.add('show-options');

      return;
    }
  }

  // Color Contrast Selections
  colorContrastSelected(color) {
    let colorButtons = this.$el.querySelectorAll('#btn-color-scheme .tool-button');

    // Change selection
    colorButtons.forEach((btn) => {
      if (btn.getAttribute('id') == color) {
        btn.classList.add('selected');
      } else {
        btn.classList.remove('selected');
      }
    });

    this.playerEmitter?.emit(EventBusConstants.PNP_ITEM_CLICKED, [['name', `color-scheme.${color}`]]);
  }

  lineReaderSelected() {
    let btn = this.$el.querySelector('#btn-line-reader');
    this.$el.querySelector('.menu-button.show-options')?.classList.remove('show-options');

    if (btn.classList.contains('selected')) {
      btn.classList.remove('selected');
      btn.classList.remove('show-options');
      this.playerEmitter?.emit(EventBusConstants.PNP_ITEM_CLICKED, [['name', 'line-reader.off']]);
    } else {
      btn.classList.add('selected');
      btn.classList.add('show-options');
      this.playerEmitter?.emit(EventBusConstants.PNP_ITEM_CLICKED, [['name', 'line-reader.on']]);
    }
  }

  showDictionary() {
    TextHelp.getAPI().then((api) => {
      api.textTools.dictionaryLookup();
    });
  }

  fontSizeSelected(size) {
    let fontButtons = this.$el.querySelectorAll('#btn-font-size .tool-button');

    fontButtons.forEach((btn) => {
      console.log(btn.getAttribute('id'), size);
      if (btn.getAttribute('id') == `font-size-${size}`) {
        btn.classList.add('selected');
      } else {
        btn.classList.remove('selected');
      }
    });

    this.playerEmitter?.emit(EventBusConstants.PNP_ITEM_CLICKED, [['name', `font-size.${size}`]]);
  }

  zoomSelected(option) {
    let zoomLevel = this.$el.querySelector('#zoom-level');
    let curZoom = parseInt(zoomLevel.innerText.replace('%', ''));

    if (option == 'decrease') {
      curZoom = curZoom > 50 ? curZoom - 25 : 50;
    } else if (option == 'increase') {
      curZoom = curZoom < 150 ? curZoom + 25 : 150;
    } else {
      curZoom = 100;
    }
    this.playerEmitter?.emit(EventBusConstants.PNP_ITEM_CLICKED, [['name', `zoom.${curZoom}%`]]);
    zoomLevel.innerText = curZoom + '%';
    return;
  }

  choiceMaskingSelected() {
    let btn = this.$el.querySelector('#btn-choice-masking');
    this.$el.querySelector('.menu-button.show-options')?.classList.remove('show-options');

    if (btn.classList.contains('selected')) {
      btn.classList.remove('selected');
      btn.classList.remove('show-options');
      this.playerEmitter?.emit(EventBusConstants.PNP_ITEM_CLICKED, [['name', 'choice-masking.off']]);
    } else {
      btn.classList.add('selected');
      btn.classList.add('show-options');
      this.playerEmitter?.emit(EventBusConstants.PNP_ITEM_CLICKED, [['name', 'choice-masking.on']]);
    }
  }

  choiceMaskingReset() {
    this.playerEmitter?.emit(EventBusConstants.PNP_ITEM_CLICKED, [['name', 'choice-masking.reset']]);
  }

  strikethroughText() {
    TextHelp.getAPI().then((api) => api.studyTools.strikethroughText());
  }

  highlightText(color: string) {

    console.log('highlightSelected', color);

    switch (color) {
      case 'cyan':
        TextHelp.getAPI().then((api) => api.studyTools.highlightCyan());
        break;
      case 'magenta':
        TextHelp.getAPI().then((api) => api.studyTools.highlightMagenta());
        break;
      case 'green':
        TextHelp.getAPI().then((api) => api.studyTools.highlightGreen());
        break;
      default:
        TextHelp.getAPI().then((api) => api.studyTools.highlightYellow());
        break;
    }
    /*var selection = window.getSelection()?.getRangeAt(0);
    if (!selection) return;
    console.log(selection);
    var selectedText = selection.extractContents();
    var span = document.createElement('span');
    span.style.backgroundColor = color;
    span.appendChild(selectedText);
    selection.insertNode(span);*/
  }

  clearHighlights() {
    TextHelp.getAPI().then((api) => api.studyTools.clearHighlights());
  }

  private togglePnpTool(name: string): void {
    this.playerEmitter?.emit(EventBusConstants.PNP_ITEM_CLICKED, [['name', name]]);
    this.toolManager!.toggleTool(name);
  }

  private expandList(): void {
    const element = this.$el.querySelector('#toolbar-container');
    element?.classList.remove('expanded-list');
    if (!this.expanded) {
      element?.classList.add('expanded-list');
    }

    const toggleElement = this.$el.querySelector('#toggle-icon');
    toggleElement?.classList.remove('fa-circle-chevron-left', 'fa-circle-chevron-right');
    toggleElement?.classList.add(!this.expanded ? 'fa-circle-chevron-right' : 'fa-circle-chevron-left');

    this.expanded = !this.expanded;

    const toggleListButton: HTMLElement = this.$el.querySelector('#toggle-list-button');
    if (toggleListButton) {
      toggleListButton.title = this.expanded ? '' : this.$t('btn.expand_menu');
    }
  }

  private get hideExpandButton(): boolean {
    return this.hideExpandButton_ || this.toolManager!.getPnpArray().length === 0;
  }

  private windowSizeChanged() {
    const minimumSize = this.focusMode ? 1879 : 1840;
    this.hideExpandButton_ = window.innerWidth > minimumSize;
    if (window.innerWidth > minimumSize && !this.expanded) {
      this.expandList();
    } else if (window.innerWidth <= minimumSize && this.expanded) {
      this.expandList();
    }
  }
}
